import { useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import type { StaffSearchResult } from '@/api/occerSearchApi';

interface AppraiseeSearchSectionProps {
  staff: StaffSearchResult[];
  isLoading: boolean;
  value: StaffSearchResult | null;
  onChange: (value: StaffSearchResult | null) => void;
}

const staffLabel = (staff: StaffSearchResult) =>
  `${staff.firstName} ${staff.lastName} — ${staff.tenureInGrade}`;

export function AppraiseeSearchSection({
  staff,
  isLoading,
  value,
  onChange,
}: AppraiseeSearchSectionProps) {
  // Present as a text box with typeahead: no dropdown arrow, and the suggestion list only
  // appears while typing (never a full-list dropdown on focus).
  const [open, setOpen] = useState(false);
  return (
    <Section>
      <SectionHeader>APPRAISEE SEARCH</SectionHeader>
      <SectionCard>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', py: 2 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap', pr: 2 }}>
              Choose a person:
            </Typography>
            <Autocomplete
              id="appraisee-search"
              sx={{ width: '100%', maxWidth: 360 }}
              options={staff}
              getOptionLabel={staffLabel}
              value={value}
              onChange={(_, newValue) => onChange(newValue)}
              isOptionEqualToValue={(option, selected) => option.employeeId === selected.employeeId}
              loading={isLoading}
              noOptionsText="No people found"
              forcePopupIcon={false}
              open={open}
              onClose={() => setOpen(false)}
              onInputChange={(_, inputValue, reason) => {
                // Open the suggestion list only while the user is typing (>=1 char).
                setOpen(reason === 'input' && inputValue.trim().length >= 1);
              }}
              filterOptions={(options, state) => {
                const input = state.inputValue.trim().toLowerCase();
                if (input.length < 1) {
                  return [];
                }
                return options.filter((option) =>
                  `${option.firstName} ${option.lastName} ${option.tenureInGrade}`
                    .toLowerCase()
                    .includes(input),
                );
              }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Name and Surname"
                  placeholder="Start typing to search..."
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true },
                  }}
                />
              )}
            />
          </Box>
      </SectionCard>
    </Section>
  );
}

export default AppraiseeSearchSection;
